import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Honeybadger } from '@honeybadger-io/react';
import { useQuery } from '@tanstack/react-query';
import { Formik } from 'formik';
import { useRecoilState } from 'recoil';

import { requestApi } from 'api/Api';

import { ia } from 'lib/helpers/utility';

import practiceState from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Loading from 'components/shared/Loading/Loading';

import AccountForm from './components/AccountForm';
import formInitialValues from './lib/formInitialValues';
import formValidationSchema from './lib/formValidationSchema';

const Account = () => {
  const addressRef = useRef();
  const [practice, setPractice] = useRecoilState(practiceState.currentPractice);
  const [loading, setLoading] = useState(false);
  const [showCurrencyInput, setShowCurrencyInput] = useState(false);
  const [selectedSurchargeButton, setSelectedSurchargeButton] = useState();
  const [isServicesAvailable, setIsServicesAvailable] = useState(false);
  const [showTechnologyModal, setShowTechnologyModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getPractice();
  }, []);

  const { data } = useQuery({
    queryKey: ['getServices'],
    queryFn: getServices,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    const isAvailable = data?.services.some(
      (service) =>
        service?.state === 'active' && service.self_scheduling && ia(service?.practitioner_ids)
    );
    setIsServicesAvailable(isAvailable);
  }, [data]);

  async function getServices() {
    const response = await requestApi({
      url: '/api/practice/services/get',
      navigate,
      params: {
        offset: 0,
        limit: null
      }
    });
    return response;
  }

  const getPractice = async () => {
    setLoading(true);
    try {
      const res = await requestApi({ url: '/api/practice/current_practice', params: {}, navigate });
      const { redirect, practice: loadedPractice } = res;
      if (loadedPractice) {
        setPractice((prev) => ({
          ...loadedPractice,
          ...prev
        }));
        rememberSelected(loadedPractice.surcharge_percentage);
      } else {
        navigate(redirect);
      }
    } catch (error) {
      Honeybadger.notify(
        `practice: ${practice.id}, file: /practice/settings/account/index.js, method: getPractice, error:${error}`
      );
      console.error(error);
    }
    setLoading(false);
  };

  const surchargeOptions = [1, 2, 3, 4, 'Custom'];

  const rememberSelected = (btn) => {
    if (Number(btn) == 0) {
      setSelectedSurchargeButton(-1);
    } else if (!isNaN(btn) && surchargeOptions.includes(Number(btn))) {
      setSelectedSurchargeButton(Number(btn - 1));
    } else if (!isNaN(btn) && !surchargeOptions.includes(btn)) {
      setSelectedSurchargeButton(4);
      setShowCurrencyInput(true);
    }
  };

  const saveChanges = async (values) => {
    setLoading(true);

    await addressRef.current?.updateAddress();

    const updateFields = {
      display_settings: values?.displaySettings,
      header_photo: values?.headerPhoto,
      dba_name: values?.dbaName,
      surcharge_enabled:
        values?.surchargeEnabled !== null ? values?.surchargeEnabled : practice?.surcharge_enabled,
      surcharge_percentage:
        values?.surchargeAmount !== null
          ? parseFloat(values?.surchargeAmount / 100)
          : parseFloat(practice?.surcharge_percentage || 0)
    };

    try {
      const res = await requestApi({
        url: '/api/practice/update',
        navigate,
        params: {
          practice_id: practice.id,
          fields: updateFields
        }
      });

      if (res.code === 0) {
        setPractice((prev) => ({
          ...prev,
          ...updateFields
        }));
        showAlert({
          message: 'Changes made successfully',
          color: 'success'
        });
      } else {
        Honeybadger.notify(
          `practice: ${practice.id}, file: /practice/settings/account/index.js, method: saveChanges (surcharge update), error`
        );
      }
    } catch (error) {
      Honeybadger.notify(
        `practice: ${practice.id}, file: /practice/settings/account/index.js, method: saveChanges (surcharge update), error: ${error}`
      );
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <Formik
          initialValues={formInitialValues(practice)}
          enableReinitialize={true}
          validationSchema={formValidationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            saveChanges(values);
          }}>
          {({ submitForm }) => (
            <div className="flex h-full flex-col">
              <div className="flex flex-shrink-0 items-center justify-between border-b border-l-0 border-r-0 border-t-0 border-solid border-b-neutral-100 pr-6">
                <span className="px-6 py-3 text-lg text-primary-800">Account</span>
                <Button text="Save Changes" size="small" onClick={() => submitForm()} />
              </div>
              <AccountForm
                practice={practice}
                setPractice={setPractice}
                isServicesAvailable={isServicesAvailable}
                addressRef={addressRef}
                setShowTechnologyModal={setShowTechnologyModal}
                selectedSurchargeButton={selectedSurchargeButton}
                showCurrencyInput={showCurrencyInput}
                setShowCurrencyInput={setShowCurrencyInput}
                setSelectedSurchargeButton={setSelectedSurchargeButton}
                showTechnologyModal={showTechnologyModal}
                saveChanges={saveChanges}
              />
            </div>
          )}
        </Formik>
      )}
    </>
  );
};

export default withErrorBoundary(Account);
